import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchAudios } from "@/lib/api";
import { ytId } from "@/components/AudioMedia";
import { useAudioPlayer } from "@/context/AudioPlayerContext";
import { Music, ArrowUpRight, Play, AudioLines, Youtube } from "lucide-react";

// Reusable "Listen" section used on the homepage and festival pages.
// placement: "home" | "navratri" | ... Shows the admin-curated audio(s) flagged
// for that surface; tapping launches the global sticky player (continuous play).
export default function AudioFeature({ placement, title, subtitle, max = 1 }) {
  const { pick } = useLang();
  const { play, track, playing } = useAudioPlayer();
  const [items, setItems] = useState([]);
  useEffect(() => { fetchAudios(placement).then((d) => setItems((d || []).slice(0, max))).catch(() => setItems([])); }, [placement, max]);
  if (!items.length) return null;
  const main = items[0];
  const mainTitle = pick(main.title_hi || main.title, main.title);
  const yid = main.kind === "youtube" ? ytId(main.url) : "";
  const thumb = yid ? `https://img.youtube.com/vi/${yid}/hqdefault.jpg` : "";
  const isActive = track && track.id === main.id;

  return (
    <section className="mx-auto max-w-5xl px-5 pt-14 md:px-8 md:pt-20" data-testid={`audio-feature-${placement}`}>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline-label text-saffron flex items-center gap-2"><Music className="h-3.5 w-3.5" /> {pick("सुनें", "Listen")}</p>
          <h2 className="mt-2 font-serif-latin text-2xl text-ink md:text-3xl">{title || mainTitle}</h2>
          {subtitle && <p className="mt-1 text-ink-2">{subtitle}</p>}
        </div>
        <Link to="/audios" data-testid={`audio-feature-all-${placement}`} className="inline-flex shrink-0 items-center gap-1.5 border border-saffron px-4 py-2 text-sm font-medium text-saffron transition-colors hover:bg-[var(--saffron)] hover:text-white">
          {pick("सभी ऑडियो", "All audio")} <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <button data-testid={`audio-feature-play-${placement}`} onClick={() => play({ ...main, title: mainTitle })}
        className={`group flex w-full items-center gap-4 overflow-hidden border bg-surface p-3 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--gold)] hover:shadow-lg ${isActive ? "border-[var(--gold)] ring-2 ring-[var(--gold)]/30" : "border-line"}`}>
        <div className="relative h-20 w-32 shrink-0 overflow-hidden rounded bg-[var(--parchment)] md:h-24 md:w-40">
          {thumb ? (
            <img src={thumb} alt={mainTitle} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[var(--saffron)]/90"><AudioLines className="h-8 w-8 text-white/80" /></div>
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/20"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--saffron)] text-white shadow-lg transition-transform group-hover:scale-110"><Play className="h-4 w-4" fill="currentColor" /></span></span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-medium text-saffron">{main.kind === "youtube" ? <Youtube className="h-3 w-3" /> : <AudioLines className="h-3 w-3" />} {main.category || pick("ऑडियो", "Audio")}</p>
          <p className="mt-1 truncate font-serif-latin text-lg text-ink">{mainTitle}</p>
          <p className="mt-0.5 text-sm text-ink-3">{isActive && playing ? pick("अभी चल रहा है", "Now playing") : pick("सुनने के लिए टैप करें", "Tap to listen — keeps playing as you browse")}</p>
        </div>
      </button>
    </section>
  );
}
